import React, { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Avatar,
  Button,
  Tooltip,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  Home as HomeIcon,
  Receipt as ReceiptIcon,
  QrCodeScanner as QrCodeScannerIcon,
  CameraAlt as CameraAltIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.05)',
}));

const LogoTypography = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  letterSpacing: '0.5px',
  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginRight: theme.spacing(2),
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  fontWeight: active ? 600 : 500,
  position: 'relative',
  '&::after': active ? {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '30%',
    height: '3px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '3px 3px 0 0',
  } : {},
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

function Navbar() {
  const { currentUser, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const getInitials = () => {
    if (currentUser?.first_name && currentUser?.last_name) {
      return `${currentUser.first_name[0]}${currentUser.last_name[0]}`.toUpperCase();
    } else if (currentUser?.username) {
      return currentUser.username[0].toUpperCase();
    }
    return 'U';
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={handleDrawerToggle}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LogoTypography variant="h6" component="div">
          HouseHoldr
        </LogoTypography>
      </Box>
      <Divider />
      <List>
        <ListItem button component={RouterLink} to="/" selected={isActive('/')}>
          <ListItemIcon>
            <HomeIcon color={isActive('/') ? 'primary' : 'inherit'} />
          </ListItemIcon>
          <ListItemText primary="Home" />
        </ListItem>
        
        {isAuthenticated ? (
          <>
            <ListItem button component={RouterLink} to="/inventory" selected={isActive('/inventory')}>
              <ListItemIcon>
                <InventoryIcon color={isActive('/inventory') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Inventory" />
            </ListItem>
            
            <ListItem button component={RouterLink} to="/shopping-list" selected={isActive('/shopping-list')}>
              <ListItemIcon>
                <ShoppingCartIcon color={isActive('/shopping-list') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Shopping List" />
            </ListItem>
            
            <ListItem button component={RouterLink} to="/ocr-upload" selected={isActive('/ocr-upload')}>
              <ListItemIcon>
                <ReceiptIcon color={isActive('/ocr-upload') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Scan Receipt" />
            </ListItem>
            
            <ListItem button component={RouterLink} to="/barcode-scan" selected={isActive('/barcode-scan')}>
              <ListItemIcon>
                <QrCodeScannerIcon color={isActive('/barcode-scan') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Scan Barcode" />
            </ListItem>
            
            <ListItem button component={RouterLink} to="/product-recognition" selected={isActive('/product-recognition')}>
              <ListItemIcon>
                <CameraAltIcon color={isActive('/product-recognition') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Scan Product" />
            </ListItem>
            
            <Divider sx={{ my: 1 }} />
            
            <ListItem button component={RouterLink} to="/profile" selected={isActive('/profile')}>
              <ListItemIcon>
                <PersonIcon color={isActive('/profile') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Profile" />
            </ListItem>
            
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItem>
          </>
        ) : (
          <>
            <ListItem button component={RouterLink} to="/login" selected={isActive('/login')}>
              <ListItemIcon>
                <LoginIcon color={isActive('/login') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItem>
            
            <ListItem button component={RouterLink} to="/register" selected={isActive('/register')}>
              <ListItemIcon>
                <PersonAddIcon color={isActive('/register') ? 'primary' : 'inherit'} />
              </ListItemIcon>
              <ListItemText primary="Register" />
            </ListItem>
          </>
        )}
      </List>
    </Box>
  );

  return (
    <>
      <StyledAppBar position="sticky" color="default" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar disableGutters>
            {isMobile && (
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            <LogoTypography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                display: { xs: 'none', md: 'flex' },
                textDecoration: 'none',
              }}
            >
              HouseHoldr
            </LogoTypography>
            
            <LogoTypography
              variant="h6"
              noWrap
              component={RouterLink}
              to="/"
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexGrow: 1,
                textDecoration: 'none',
              }}
            >
              HouseHoldr
            </LogoTypography>
            
            {!isMobile && isAuthenticated && (
              <Box sx={{ flexGrow: 1, display: 'flex', ml: 4 }}>
                <NavButton
                  component={RouterLink}
                  to="/inventory"
                  active={isActive('/inventory') ? 1 : 0}
                >
                  Inventory
                </NavButton>
                <NavButton
                  component={RouterLink}
                  to="/shopping-list"
                  active={isActive('/shopping-list') ? 1 : 0}
                >
                  Shopping List
                </NavButton>
                <NavButton
                  component={RouterLink}
                  to="/ocr-upload"
                  active={isActive('/ocr-upload') ? 1 : 0}
                >
                  Scan Receipt
                </NavButton>
                <NavButton
                  component={RouterLink}
                  to="/barcode-scan"
                  active={isActive('/barcode-scan') ? 1 : 0}
                >
                  Scan Barcode
                </NavButton>
                <NavButton
                  component={RouterLink}
                  to="/product-recognition"
                  active={isActive('/product-recognition') ? 1 : 0}
                >
                  Scan Product
                </NavButton>
              </Box>
            )}
            
            <Box sx={{ flexGrow: isMobile || !isAuthenticated ? 1 : 0 }} />
            
            {!isMobile && !isAuthenticated && (
              <Box sx={{ display: 'flex' }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  color="primary"
                  sx={{ mr: 1 }}
                >
                  Login
                </Button>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  color="primary"
                >
                  Register
                </Button>
              </Box>
            )}
            
            {isAuthenticated && (
              <Box sx={{ flexGrow: 0 }}>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      {getInitials()}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem onClick={handleCloseUserMenu} disabled>
                    <Typography textAlign="center">
                      Hello, {currentUser?.first_name || currentUser?.username}
                    </Typography>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { handleCloseUserMenu(); navigate('/profile'); }}>
                    <ListItemIcon>
                      <PersonIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">Profile</Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography textAlign="center">Logout</Typography>
                  </MenuItem>
                </Menu>
              </Box>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Navbar;